/**
 * Lightweight FLOWS metrics (pick velocity, corrections, ad-hoc adds, maintenance lag start).
 * Stored in localStorage per tenant for later research aggregation — no PII.
 */
import { useAuthStore } from '@/stores/auth'

const KEY = 'eqsys_ops_metrics_v1'
const MAX_EVENTS = 500

function scope() {
  try {
    const auth = useAuthStore()
    const tid = auth.currentTenant?.id
    const uid = auth.user?.id
    if (tid && uid) return `${tid}:${uid}`
  } catch { /* pinia may be unavailable in unit tests */ }
  return 'anon'
}

function loadAll() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}') || {}
  } catch {
    return {}
  }
}

function saveAll(data) {
  try { localStorage.setItem(KEY, JSON.stringify(data)) } catch { /* ignore */ }
}

function pushEvent(type, payload = {}) {
  const all = loadAll()
  const s = scope()
  const list = Array.isArray(all[s]) ? all[s] : []
  list.push({ type, t: Date.now(), ...payload })
  all[s] = list.slice(-MAX_EVENTS)
  saveAll(all)
}

export function recordScanMetric({ code, ms, ok }) {
  pushEvent('scan', { ok: !!ok, ms: Math.round(ms || 0), len: String(code || '').length })
}

export function recordAdHocAdd({ projectId }) {
  pushEvent('adhoc_add', { projectId: projectId ?? null })
}

export function recordCorrection({ reason }) {
  pushEvent('correction', { reason: reason || 'unknown' })
}

export function recordMaintenanceFlag({ itemMetadataId }) {
  pushEvent('maintenance_flag', { itemMetadataId: itemMetadataId ?? null })
}

export function getOpsMetricsSummary() {
  const list = loadAll()[scope()] || []
  const scans = list.filter(e => e.type === 'scan')
  const okScans = scans.filter(e => e.ok)
  const avgMs = okScans.length
    ? Math.round(okScans.reduce((a, e) => a + (e.ms || 0), 0) / okScans.length)
    : null
  return {
    scanCount: scans.length,
    scanOkRate: scans.length ? okScans.length / scans.length : null,
    pickVelocityMs: avgMs,
    adhocAdds: list.filter(e => e.type === 'adhoc_add').length,
    corrections: list.filter(e => e.type === 'correction').length,
    maintenanceFlags: list.filter(e => e.type === 'maintenance_flag').length,
    events: list.length,
  }
}

export function useOpsMetrics() {
  return {
    recordScanMetric,
    recordAdHocAdd,
    recordCorrection,
    recordMaintenanceFlag,
    getOpsMetricsSummary,
  }
}
